import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Grid,
  Typography,
  Button,
} from '@mui/material';
import { Edit as EditIcon } from '@mui/icons-material';
import { useSelector } from 'react-redux';
import { useUser } from '@/providers';

import BaseEnvironmentField from './BaseEnvironmentField';
import CustomImageField from './CustomImageField';
import StandardEnvironmentFields from './StandardEnvironmentFields';
import PackageDependenciesField from './PackageDependenciesField';
import InitScriptField from './InitScriptField';
import ProjectCredentialsField from './ProjectCredentialsField';
import SecretsField from './SecretsField';
import EnvDrawer from '@/components/shared/EnvDrawer';
import JOB_TYPES from '@/components/jobs/types';
import cleanEmptyValues from '@/util/cleanEmptyValues';

export default function EnvironmentFields({
  initialValues,
  type,
  onChange,
  edit = false,
}) {
  const user = useUser();
  const environments = useSelector((state) => state.environments);

  const getDefaultEnvironmentType = () => {
    return type === JOB_TYPES.NOTEBOOK.value
      ? user.default_notebook_environment
      : type === JOB_TYPES.TRAINING.value
        ? user.default_training_environment
        : user.default_inference_environment;
  };

  const getVersionFromEnvironmentId = useCallback(
    (env_type) => {
      const env = environments.find((env) => env.id === env_type);
      if (env) {
        return {
          type: env_type,
          py_version: env.py_version,
          framework: env.framework,
          version: env.framework === 'Deep Learning' ? 'latest' : env.version,
        };
      }
      return {};
    },
    [environments],
  );

  const initial_state = {
    type: initialValues?.type || getDefaultEnvironmentType(),
    custom_image: initialValues?.custom_image || '',
    env: initialValues?.env || [],
    worker_key_types: initialValues?.worker_key_types || [],
    secrets: initialValues?.secrets || [],
    packages: {
      pip: initialValues?.packages?.pip || [],
      apt: initialValues?.packages?.apt || [],
    },
    init_script: initialValues?.init_script || '',
    py_version: initialValues?.py_version || '',
    framework: initialValues?.framework || '',
    version: initialValues?.version || '',
  };

  const [values, setValues] = useState(initial_state);
  const [pip_raw, setPipRaw] = useState(
    initialValues?.packages?.pip ? initialValues.packages.pip.join('\n') : '',
  );
  const [apt_raw, setAptRaw] = useState(
    initialValues?.packages?.apt ? initialValues.packages.apt.join('\n') : '',
  );
  const [custom_environment, setCustomEnvironment] = useState(
    initial_state.type === 'CUSTOM',
  );

  const [errors, setErrors] = useState({
    type_error: false,
    pip_error: false,
    apt_error: false,
    custom_image_error: false,
    custom_image_type_error: false,
    env_error: false,
  });

  const [envDrawerOpen, setEnvDrawerOpen] = useState(false);
  const [envDrawerError, setEnvDrawerError] = useState(false);
  const isInitialMount = useRef(true);

  // Initialize values from environment ID when environments load (similar to componentDidMount/componentDidUpdate)
  useEffect(() => {
    if (environments.length && values.type && !custom_environment) {
      const env_data = getVersionFromEnvironmentId(values.type);
      if (
        env_data.py_version &&
        (!values.py_version || values.py_version !== env_data.py_version)
      ) {
        setValues((prev) => ({
          ...prev,
          ...env_data,
        }));
      }
    }
  }, [
    environments.length,
    values.type,
    custom_environment,
    values.py_version,
    getVersionFromEnvironmentId,
  ]);

  // Initial mount - call onChange
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      const cleanedEnvironment = cleanEmptyValues(values);
      onChange('environment', cleanedEnvironment || {}, false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Update onChange when values or errors change
  useEffect(() => {
    if (isInitialMount.current) {
      return;
    }
    const cleanedEnvironment = cleanEmptyValues(values);
    onChange(
      'environment',
      cleanedEnvironment || {},
      errors.type_error ||
        errors.pip_error ||
        errors.apt_error ||
        errors.custom_image_error ||
        errors.env_error,
    );
  }, [values, errors, onChange]);

  const handleCustomEnvironmentChange = (checked) => {
    setCustomEnvironment(checked);
    setValues((prev) => ({
      ...prev,
      type: checked ? 'CUSTOM' : getDefaultEnvironmentType(),
      custom_image: checked ? prev.custom_image : '',
      py_version: checked ? '' : prev.py_version,
      framework: checked ? '' : prev.framework,
      version: checked ? '' : prev.version,
    }));
    if (!checked && environments.length) {
      const default_type = getDefaultEnvironmentType();
      const env_data = getVersionFromEnvironmentId(default_type);
      if (env_data.py_version) {
        setValues((prev) => ({
          ...prev,
          ...env_data,
        }));
      }
    }
  };

  const handleCustomImageChange = (new_value) => {
    setValues((prev) => ({
      ...prev,
      custom_image: new_value,
    }));
  };

  const handleCustomImageErrorChange = (error_data) => {
    setErrors((prev) => ({
      ...prev,
      ...error_data,
    }));
  };

  const handleStandardEnvironmentChange = (new_values) => {
    setValues((prev) => ({
      ...prev,
      ...new_values,
    }));
  };

  const handleTypeErrorChange = (has_error) => {
    setErrors((prev) => ({
      ...prev,
      type_error: has_error,
    }));
  };

  const handlePackageChange = (package_data) => {
    if (package_data.pip_raw !== undefined) {
      setPipRaw(package_data.pip_raw);
    }
    if (package_data.apt_raw !== undefined) {
      setAptRaw(package_data.apt_raw);
    }
    setValues((prev) => ({
      ...prev,
      packages: {
        ...prev.packages,
        ...(package_data.pip !== undefined && { pip: package_data.pip }),
        ...(package_data.apt !== undefined && { apt: package_data.apt }),
      },
    }));
  };

  const handlePackageErrorChange = (error_data) => {
    setErrors((prev) => ({
      ...prev,
      ...error_data,
    }));
  };

  const handleInitScriptChange = (new_value) => {
    setValues((prev) => ({
      ...prev,
      init_script: new_value,
    }));
  };

  const handleThirdPartyKeysChange = (new_value) => {
    setValues((prev) => ({
      ...prev,
      worker_key_types: new_value,
    }));
  };

  const handleSecretsChange = (new_value) => {
    setValues((prev) => ({
      ...prev,
      secrets: new_value,
    }));
  };

  const handleEnvDrawerClose = (new_env) => {
    setValues((prev) => ({
      ...prev,
      env: new_env,
    }));
  };

  // Sync EnvDrawer error state
  useEffect(() => {
    setErrors((prev) => ({
      ...prev,
      env_error: envDrawerError,
    }));
  }, [envDrawerError]);

  return (
    <Grid container spacing={2}>
      <Grid size={12}>
        <Typography variant='h6'>Base Image</Typography>
      </Grid>
      <Grid size={12}>
        <BaseEnvironmentField
          value={custom_environment}
          onChange={handleCustomEnvironmentChange}
          edit={edit}
        />
      </Grid>

      {custom_environment ? (
        <Grid size={12}>
          <CustomImageField
            value={values.custom_image}
            onChange={handleCustomImageChange}
            onErrorChange={handleCustomImageErrorChange}
            edit={edit}
          />
        </Grid>
      ) : (
        <Grid size={12}>
          <StandardEnvironmentFields
            py_version={values.py_version}
            framework={values.framework}
            version={values.version}
            onChange={handleStandardEnvironmentChange}
            onTypeErrorChange={handleTypeErrorChange}
            edit={edit}
          />
        </Grid>
      )}

      <Grid size={12}>
        <Typography variant='h6'>Initialization Settings</Typography>
      </Grid>

      <Grid size={12}>
        <InitScriptField
          value={values.init_script}
          onChange={handleInitScriptChange}
          edit={edit}
        />
      </Grid>

      <Grid size={12}>
        <Typography variant='body1' fontWeight='medium' sx={{ mb: 1 }}>
          Packages
        </Typography>
        <PackageDependenciesField
          pip_raw={pip_raw}
          apt_raw={apt_raw}
          pip_error={errors.pip_error}
          apt_error={errors.apt_error}
          onChange={handlePackageChange}
          onErrorChange={handlePackageErrorChange}
          edit={edit}
        />
      </Grid>

      <Grid size={12}>
        <Typography variant='h6'>Runtime Settings</Typography>
      </Grid>

      <Grid size={12}>
        <Typography variant='body1' fontWeight='medium' sx={{ mb: 1 }}>
          Environment Variables
        </Typography>
        <Button
          variant='outlined'
          startIcon={<EditIcon />}
          onClick={() => setEnvDrawerOpen(true)}
        >
          {values.env && values.env.length > 0
            ? `Manage Environment Variables (${values.env.length})`
            : 'Add Environment Variables'}
        </Button>
        <EnvDrawer
          open={envDrawerOpen}
          handleClose={() => setEnvDrawerOpen(false)}
          initialValues={values.env || []}
          setValue={handleEnvDrawerClose}
          error={envDrawerError}
          setError={setEnvDrawerError}
        />
      </Grid>

      <Grid size={12}>
        <ProjectCredentialsField
          value={values.worker_key_types}
          onChange={handleThirdPartyKeysChange}
          edit={edit}
        />
      </Grid>

      <Grid size={12}>
        <SecretsField
          value={values.secrets}
          onChange={handleSecretsChange}
          edit={edit}
        />
      </Grid>
    </Grid>
  );
}
